/**
 * 画像ファイルのバリデーション (純粋関数)。
 * ファイル I/O 不要、Canvas 不要。Vitest で完全テスト可能。
 */

import { CARSENSOR_SPEC, type Result, type ValidationError } from './types';

const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
type SupportedMimeType = (typeof SUPPORTED_MIME_TYPES)[number];

/**
 * MIME タイプが対応フォーマットか判定。
 */
export function isSupportedMimeType(mime: string): mime is SupportedMimeType {
  return (SUPPORTED_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * File のフォーマットとサイズを検証。ピクセル寸法の検証は別関数
 * (`validateDimensions`) で画像読込後に行う。
 */
export function validateFile(file: File): Result<void> {
  if (file.size === 0) {
    return failure('EMPTY_FILE', `${file.name}: ファイルが空です`, { file: file.name });
  }
  if (!isSupportedMimeType(file.type)) {
    return failure(
      'UNSUPPORTED_FORMAT',
      `${file.name}: 対応形式は JPEG / PNG のみです (受信: ${file.type || '不明'})`,
      { file: file.name, mimeType: file.type },
    );
  }
  return { ok: true, value: undefined };
}

/**
 * ファイル数が 80 枚以内か検証。
 */
export function validateFileCount(files: readonly File[]): Result<void> {
  if (files.length === 0) {
    return failure('EMPTY_FILE', '画像ファイルが選択されていません');
  }
  if (files.length > CARSENSOR_SPEC.MAX_PHOTOS_PER_VEHICLE) {
    return failure(
      'TOO_MANY_FILES',
      `最大 ${CARSENSOR_SPEC.MAX_PHOTOS_PER_VEHICLE} 枚までです (受信: ${files.length} 枚)`,
      { count: files.length },
    );
  }
  return { ok: true, value: undefined };
}

/**
 * 画像の寸法が最小要件を満たすか検証 (ImageBitmap / HTMLImageElement 取得後に呼ぶ)。
 */
export function validateDimensions(width: number, height: number, fileName?: string): Result<void> {
  if (width < CARSENSOR_SPEC.MIN_WIDTH || height < CARSENSOR_SPEC.MIN_HEIGHT) {
    return failure(
      'TOO_SMALL',
      `${fileName ?? '画像'}: 最小サイズは ${CARSENSOR_SPEC.MIN_WIDTH}×${CARSENSOR_SPEC.MIN_HEIGHT} です (受信: ${width}×${height})`,
      fileName ? { file: fileName, width, height } : { width, height },
    );
  }
  return { ok: true, value: undefined };
}

// ===== 内部ヘルパ =======================================================

function failure(
  code: ValidationError['code'],
  message: string,
  details?: Readonly<Record<string, unknown>>,
): Result<never> {
  const file = details && typeof details['file'] === 'string' ? details['file'] : undefined;
  return {
    ok: false,
    error: {
      code,
      message,
      ...(file !== undefined ? { file } : {}),
      ...(details !== undefined ? { details } : {}),
    },
  };
}
